<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row justify="space-between">
        <v-col cols="12" sm="4">
          <v-card dark color="primary">
            <v-card-title>
              <v-select
                v-model="character.equippedArmor"
                :items="armorList"
                label="Armor"
                hide-details
                return-object
                item-text="name" />
            </v-card-title>
            <v-card-text>
              <v-sheet color="secondary" class="pa-2">
                AC Bonus: {{ character.equippedArmor.ac }}
                <br>
                Type: {{ character.equippedArmor.type }}
                <br>
                Weight: {{ character.equippedArmor.weight }}
              </v-sheet>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" :sm="character.offHandDisabled ? 8 : 4">
          <v-card dark color="primary">
            <v-card-title>
              <v-select
                v-model="character.equippedMain"
                @change="mainHandChange()"
                :items="mainHandList"
                label="Main Hand"
                hide-details
                return-object
                item-text="name" />
              </v-card-title>
            <v-card-text>
              <v-sheet color="secondary" class="pa-2">
                <template v-if="character.equippedMain.class === 'Shield'">
                  AC Bonus: {{ character.equippedMain.ac }}
                  <br>
                  Features: {{ character.equippedMain.features }}
                  <br>
                  Weight: {{ character.equippedMain.weight }}
                </template>
                <template v-else>
                  Attack Bonus: +{{ attackBonus(character.equippedMain) }}
                  <br>
                  Damage: {{ damageBonus(character.equippedMain) }}
                  <br>
                  Stamina Cost: {{ character.equippedMain.stamina }}
                  <br>
                  Critical Range: {{ critRange(character.equippedMain )}} - 20
                  <br>
                  Range: {{ getRange(character.equippedMain) }}
                  <br>
                  <template v-if="character.equippedMain.class === 'Ranged'">
                    Aim Max: {{ character.equippedMain.aim }}
                    <br>
                    Upkeep Cost: {{ character.equippedMain.upkeep }}
                    <br>                        
                  </template>
                  Weight: {{ character.equippedMain.weight }}
                </template>
              </v-sheet>
            </v-card-text>
          </v-card>
        </v-col> 
        <v-col v-if="!character.offHandDisabled" cols="12" sm="4">
          <v-card dark color="primary">
            <v-card-title>
              <v-select
                v-model="character.equippedOff"
                :items="offHandList"
                label="Off Hand"
                hide-details
                return-object
                item-text="name" />
              </v-card-title>
            <v-card-text>
              <v-sheet color="secondary" class="pa-2">
                <template v-if="character.equippedOff.class === 'Shield'">
                  AC Bonus: {{ character.equippedOff.ac }}
                  <br>
                  Features: {{ character.equippedOff.features }}
                  <br>
                  Weight: {{ character.equippedOff.weight }}
                  <br>
                  Critical Range: {{ critRange(character.equippedOff )}} - 20
                  <br>
                </template>
                <template v-else>
                  Attack Bonus: +{{ attackBonus(character.equippedOff) }}
                  <br>
                  Damage: {{ damageBonus(character.equippedOff) }}
                  <br>
                  Stamina Cost: {{ character.equippedOff.stamina }}
                  <br>
                  Critical Range: {{ critRange(character.equippedOff )}} - 20
                  <br>
                  Range: {{ getRange(character.equippedOff) }}
                  <br>
                  <template v-if="character.equippedOff.class === 'Ranged'">
                    Aim Max: {{ character.equippedOff.aim }}
                    <br>
                    Upkeep Cost: {{ character.equippedOff.upkeep }}
                    <br>                        
                  </template>
                  Weight: {{ character.equippedOff.weight }}
                </template>
              </v-sheet>
            </v-card-text>
          </v-card>
        </v-col>              
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';

export default {
  props: {
    character: {
      type: Object,
      required: true
    }
  },
  methods: {
    mainHandChange() {
      if ((this.character.equippedMain.class === 'Ranged' && this.character.equippedMain.type == 'Missile') || this.character.equippedMain.size === 'Heavy') {
        this.character.equippedOff = this.offHandList.find(item => item.name === 'Unarmed');
        this.character.offHandDisabled = true;
      } else {
        this.character.offHandDisabled = false;
      }
    },
    attackBonus(weapon) {
      let attackBonus = 0;
      if (weapon.class === 'Ranged') {
        attackBonus +=  this.skillTotal({ name: 'Attack(Ranged)', stat: 'Finesse-Mind' });
        if (this.character.traits.includes('Focused Archer') && !this.encumbrance) {
          attackBonus += Math.floor(this.character.spirit / 2);
        }
      } else {
        attackBonus += this.skillTotal({ name: 'Attack(Melee)', stat: 'Physique-Finesse' });
      }
      attackBonus += this.traitCounter('Weapon Proficiency');
      if (this.character.traits.includes('Insightful Combatant')) {
        attackBonus += 2;
      }
      if (this.character.traits.includes('Attune Self') && !this.encumbrance && this.character.equippedArmor.name === 'Unarmored') {
        attackBonus += Math.floor(this.character.spirit / 2);
      }
      if (this.character.traits.includes('Duelist') && !this.offHandDisabled && (this.character.equippedMain.name === 'Unarmed' || this.character.equippedOff.name === 'Unarmed')) {
        attackBonus += Math.floor(this.character.mind / 2);
      }
      if(weapon.class === 'Bludgeon') {
        attackBonus -= 1;
      }
      if(weapon.class === 'Axe') {
        attackBonus += 1;
      }
      return attackBonus;
    },
    damageBonus(weapon) {
      let damageRoll = weapon.damage;
      if (weapon.class === 'Unarmed') {
        let brawlPhysique = this.character.physique;
        if (this.character.traits.includes('Brawler')) {
          brawlPhysique += 5;
        }
        if (brawlPhysique > 30) {
          damageRoll = '2d10';
        } else if (brawlPhysique > 20) {
          damageRoll = '2d8';
        } else if (brawlPhysique > 15) {
          damageRoll = '2d6';
        } else if (brawlPhysique > 10) {
          damageRoll = '2d4';
        } else if (brawlPhysique > 5) {
          damageRoll = '1d6';
        }
      }

      let damBonus = this.traitCounter('Weapon Mastery') * 2;

      if (weapon.class === 'Ranged') {
        if (weapon.type === 'Thrown') {
          damBonus += Math.floor(this.character.physique / 2);
        } else {
          if (this.character.traits.includes('Powerful Draw') && this.character.physique > this.character.finesse) {
            damBonus += Math.floor(this.character.physique / 2);
          } else {
            if (this.character.traits.includes('Deadeye')) {
              damBonus += this.character.finesse;
            } else {
              damBonus += Math.floor(this.character.finesse / 2);
            }
          }
        }
      } else {
        if (weapon.size === 'Small') {
          if (this.character.traits.includes('Dexterous Damage')) {
            damBonus += this.character.finesse;
          } else {
            damBonus += Math.floor(this.character.finesse / 2);
          }
        } else if (weapon.size === 'Medium') {
          damBonus += Math.floor(this.character.finesse / 2);
          damBonus += Math.floor(this.character.physique / 2);
        } else if (weapon.size === 'Heavy') {
          if (this.character.traits.includes('Finesse Master') && this.character.finesse > this.character.phsyique) {
            damBonus += this.character.finesse;
          } else {
            damBonus += this.character.physique;
          }
        }
      }

      if (this.character.traits.includes('Fine Strike') && (weapon.class === 'Ranged' || weapon.size === 'Small')) {
        damBonus += 2;
      }
      if (this.character.traits.includes('Insightful Strike')) {
        damBonus += 2;
      }
      if (this.character.traits.includes('Empower Self') && this.character.equippedArmor.name === 'Unarmored') {
        damBonus += Math.floor(this.character.spirit / 2);
      }
      if (this.character.traits.includes('Powerful Duelist') && !this.offHandDisabled && (this.character.equippedMain.name === 'Unarmed' || this.character.equippedOff.name === 'Unarmed')) {
        damBonus += Math.floor(this.character.mind / 2);
      }
      return damageRoll.concat(' + ', String(damBonus));
    },
    critRange(weapon) {
      let critRange = 20;
      if (this.character.traits.includes('Improved Critical')) {
        critRange -= 1;
      }
      if (this.character.traits.includes('Sneak Attack')) {
        critRange -= 1;
      }
      if (this.character.traits.includes('Master Sneak Attack')) {
        critRange -= 1;
      }
      if(weapon.class === 'Curved') {
        critRange -= 1;
      }
      return critRange;
    },
    getRange(weapon) {
      let range = weapon.range ? weapon.range : 1;
      if (weapon.type === 'Thrown' && this.character.traits.includes('Hurl') && this.character.physique > weapon.range) {
        return this.character.physique;
      }
      if (weapon.class === 'Polearm') {
        range += 1;
      }
      return range;
    },
    skillBonus(skill) {
      let bonus = 0;
      if (skill.stat === 'Physique' && this.character.traits.includes('Mighty')) {
        bonus += 1;
      }
      if (skill.stat === 'Finesse' && this.character.traits.includes('Limber')) {
        bonus += 1;
      }
      if (skill.stat === 'Mind' && this.character.traits.includes('Clever')) {
        bonus += 1;
      }
      if (skill.stat === 'Spirit' && this.character.traits.includes('Spiritual')) {
        bonus += 1;
      }
      if (!skill.name.includes('Attack') && this.character.traits.includes('Natural')) {
        bonus += 1;
      }
      return bonus;
    },
    skillTotal(skill) {
      return  (this.character.skills.values[skill.name] ? this.character.skills.values[skill.name] : 0) + this.skillBonus(skill);
    },
    traitCounter(findTrait) {
      return this.character.traits.filter(trait => trait.includes(findTrait)).length;
    }
  },
  computed: {
    ...mapGetters('actsContent', ['contentItem']),
    armorList() {
      return this.contentItem('table|8-3').items;
    },
    mainHandList() {
      return this.handsList.filter(item => item.class !== 'Shield' && item.damage !== 'Hybrid');
    },
    offHandList() {
      return this.handsList.filter(item => (item.class !== 'Ranged' || item.type === 'Thrown') && item.damage !== 'Hybrid' && item.size !== 'Heavy');
    },
    handsList() {
      let melee = this.contentItem('table|8-1').items;
      let ranged = this.contentItem('table|8-2').items.map(ranged => ({ ...ranged, class: 'Ranged' }));
      let shields = this.contentItem('table|8-4').items.map(shield => ({ ...shield, class: 'Shield' }));
      return melee.concat(ranged).concat(shields);
    }
  }
};
</script>
